/**
 * PostHog Analytics Service
 *
 * Fetches real analytics data via a Netlify Function proxy.
 * The PostHog Personal API Key never leaves the server.
 */

const PROXY_BASE = '/.netlify/functions/posthog-proxy';

class PostHogAnalyticsService {
  constructor() {
    this.cache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
    this.proxyAvailable = null; // null = unknown, true/false after first call
  }

  /**
   * Call the serverless proxy with caching.
   * Returns null when the proxy is unavailable (e.g. local dev without netlify dev).
   */
  async executeQuery(queryType, params = {}) {
    // Once we know the proxy is down, skip all further requests this session
    if (this.proxyAvailable === false) {
      return null;
    }

    const cacheKey = JSON.stringify({ queryType, params });
    const cached = this.cache.get(cacheKey);

    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.data;
    }

    try {
      const searchParams = new URLSearchParams({ query: queryType, ...params });
      const response = await fetch(`${PROXY_BASE}?${searchParams}`);

      // Detect missing proxy (Parcel/dev server returns HTML 404)
      const contentType = response.headers.get('content-type') || '';
      if (!response.ok || !contentType.includes('application/json')) {
        if (response.status === 404 || contentType.includes('text/html')) {
          console.warn('PostHog proxy not available — analytics data will be skipped.');
          this.proxyAvailable = false;
          return null;
        }
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody.error || `Proxy error: ${response.status}`);
      }

      this.proxyAvailable = true;
      const data = await response.json();

      this.cache.set(cacheKey, {
        data: data.results,
        timestamp: Date.now(),
      });

      return data.results;
    } catch (error) {
      // Network errors (fetch itself fails) — proxy unreachable
      if (error instanceof TypeError && error.message.includes('fetch')) {
        console.warn('PostHog proxy unreachable — analytics data will be skipped.');
        this.proxyAvailable = false;
        return null;
      }
      console.error('PostHog Analytics Service Error:', error);
      throw error;
    }
  }

  /**
   * Get view count for a specific blog post by ID or slug
   */
  async getBlogViewCount(blogIdentifier, useId = false) {
    try {
      const results = await this.executeQuery('blogViewCount', {
        blogIdentifier,
        useId,
      });
      if (!results) return 0;
      return results.length > 0 ? results[0][0] : 0;
    } catch (error) {
      console.warn(`Failed to fetch view count for blog ${blogIdentifier}:`, error);
      return 0;
    }
  }

  /**
   * Get view count for a specific blog post by ID (preferred method)
   */
  async getBlogViewCountById(blogId) {
    return this.getBlogViewCount(blogId, true);
  }

  /**
   * Get view counts for all blog posts
   */
  async getAllBlogViewCounts() {
    try {
      const results = await this.executeQuery('allBlogViewCounts');
      if (!results) return {};

      const viewCounts = {};
      results.forEach(([blogId, slug, count]) => {
        if (slug) {
          viewCounts[slug] = (viewCounts[slug] || 0) + count;
        }
      });

      return viewCounts;
    } catch (error) {
      console.warn('Failed to fetch all blog view counts:', error);
      return {};
    }
  }

  /**
   * Get total page views for the site
   */
  async getTotalPageViews(days = 30) {
    try {
      const results = await this.executeQuery('totalPageViews', { days });
      if (!results) return 0;
      return results.length > 0 ? results[0][0] : 0;
    } catch (error) {
      console.warn('Failed to fetch total page views:', error);
      return null;
    }
  }

  /**
   * Get popular blog posts by view count
   */
  async getPopularBlogPosts(limit = 5) {
    try {
      const results = await this.executeQuery('popularBlogPosts', { limit });
      if (!results) return [];

      return results.map(([blogId, slug, title, count]) => ({
        id: blogId && blogId !== 'null' ? blogId : null,
        slug,
        title,
        viewCount: count,
      }));
    } catch (error) {
      console.warn('Failed to fetch popular blog posts:', error);
      return [];
    }
  }

  /**
   * Get daily blog views for the last N days
   */
  async getDailyBlogViews(days = 7) {
    try {
      const results = await this.executeQuery('dailyBlogViews', { days });
      if (!results) return [];

      return results.map(([date, count]) => ({
        date,
        views: count,
      }));
    } catch (error) {
      console.warn('Failed to fetch daily blog views:', error);
      return [];
    }
  }

  /**
   * Clear the cache
   */
  clearCache() {
    this.cache.clear();
  }
}

// Export singleton instance
export const posthogAnalytics = new PostHogAnalyticsService();
export default posthogAnalytics;
